import { Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { catchError, filter, map, mergeMap, take } from "rxjs/operators";
import {
  CategoryLookup,
  Expression,
} from "../../category-builder/category-builder.models";
import { CategoryModelUtils } from "../../category-builder/category-model-utilities";
import { WorkingCategoryState } from "../../category-builder/state/working-category.reducer";
import { selectWorkingCategoryState } from "../../category-builder/state/working-category.selectors";
import { selectDataSources } from "../../data-source/state/data-source.selectors";
import { DataSourceSummary } from "../../data-source/state/union.model";
import { EaredDoveService } from "../../eared-dove/eared-dove.service";
import {
  AppliedModel,
  AppliedVersion,
  CommonModelType,
  Model,
  ModelsApplied,
  ModelState,
  ModelVersion,
} from "../../models/state/model";
import {
  selectModels,
  selectModelVersions,
} from "../../models/state/model.selectors";
import {
  Context,
  ContextKey,
  ContextParams,
  ModelContext,
} from "./context.model";
import {
  selectCategoryModelContext,
  selectDataSourceContext,
  selectEmotionModelContext,
  selectIntentModelContext,
  selectSentimentModelContext,
  selectTopicModelContext,
} from "./context.selector";
import { ContextUtils } from "../context-utilities";

@Injectable({
  providedIn: "root",
})
export class ContextService {
  dataSources: DataSourceSummary[] = [];
  models: Model[] = [];
  modelVersions: ModelVersion[] = [];

  dataSourceContext$ = new BehaviorSubject<DataSourceSummary>(null);
  categoryModelContext$ = new BehaviorSubject<ModelContext>(null);
  sentimentModelContext$ = new BehaviorSubject<ModelContext>(null);
  topicModelContext$ = new BehaviorSubject<ModelContext>(null);
  emotionModelContext$ = new BehaviorSubject<ModelContext>(null);
  intentModelContext$ = new BehaviorSubject<ModelContext>(null);
  allContext$ = new BehaviorSubject<Context>(null);
  workingCategory$ = new BehaviorSubject<Expression>(null);
  categoryList$ = new BehaviorSubject<Expression[]>(null);
  categoryLookup$ = new BehaviorSubject<CategoryLookup>(null);

  selectableModels: CommonModelType[] = [
    CommonModelType.SENTIMENT,
    CommonModelType.TOPIC,
    CommonModelType.EMOTION,
    CommonModelType.INTENT,
    CommonModelType.CATEGORY,
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store,
    private earedDove: EaredDoveService,
  ) {
    this.store
      .select(selectDataSources)
      .subscribe((ds) => (this.dataSources = ds ?? []));

    this.store
      .select(selectModelVersions)
      .subscribe((modelVersions) => (this.modelVersions = modelVersions ?? []));

    this.store
      .select(selectModels)
      .subscribe((models) => (this.models = models ?? []));

    this.route.queryParams.subscribe((queryParams: ContextParams) => {
      if (queryParams.f) {
        localStorage.setItem(queryParams.f, JSON.stringify(queryParams));
      }
    });

    this.store
      .select(selectDataSourceContext)
      .pipe(filter((dataSourceContext) => !!dataSourceContext))
      .subscribe((dataSourceContext) =>
        this.dataSourceContext$.next(dataSourceContext),
      );

    this.store
      .select(selectCategoryModelContext)
      .pipe(
        mergeMap((modelContext) => {
          if (modelContext) return this.injectVersionData(modelContext);
          if (!modelContext) return of(modelContext);
        }),
      )
      .subscribe((modelContext) =>
        this.categoryModelContext$.next(modelContext),
      );

    this.store
      .select(selectSentimentModelContext)
      .pipe(
        filter((modelContext) => !!modelContext?.version?.uuid),
        mergeMap((modelContext) => this.injectVersionData(modelContext)),
      )
      .subscribe((modelContext) =>
        this.sentimentModelContext$.next(modelContext),
      );

    this.store
      .select(selectTopicModelContext)
      .pipe(
        filter((modelContext) => !!modelContext?.version?.uuid),
        mergeMap((modelContext) => this.injectVersionData(modelContext)),
      )
      .subscribe((modelContext) => this.topicModelContext$.next(modelContext));

    this.store
      .select(selectEmotionModelContext)
      .pipe(
        filter((modelContext) => !!modelContext?.version?.uuid),
        mergeMap((modelContext) => this.injectVersionData(modelContext)),
      )
      .subscribe((modelContext) =>
        this.emotionModelContext$.next(modelContext),
      );

    this.store
      .select(selectIntentModelContext)
      .pipe(
        filter((modelContext) => !!modelContext?.version?.uuid),
        mergeMap((modelContext) => this.injectVersionData(modelContext)),
      )
      .subscribe((modelContext) => this.intentModelContext$.next(modelContext));

    combineLatest([
      this.dataSourceContext$,
      this.categoryModelContext$,
      this.sentimentModelContext$,
      this.topicModelContext$,
      this.emotionModelContext$,
      this.intentModelContext$,
    ])
      .pipe(
        map(([ds, c, s, t, e, i]) => {
          return {
            [ContextKey.DataSourceContextKey]: ds,
            [ContextKey.CategoryContextKey]: c,
            [ContextKey.SentimentContextKey]: s,
            [ContextKey.TopicContextKey]: t,
            [ContextKey.EmotionContextKey]: e,
            [ContextKey.IntentContextKey]: i,
          };
        }),
      )
      .subscribe((allContext) => this.allContext$.next(allContext));

    this.categoryModelContext$
      .pipe(
        filter((modelContext) => !!modelContext?.version?.uuid),
        map((modelContext) => {
          return CategoryModelUtils.getCategoryList(
            modelContext.version.version_data.category_tree,
          );
        }),
      )
      .subscribe((categoryList) => this.categoryList$.next(categoryList));

    this.categoryModelContext$
      .pipe(
        filter((modelContext) => !!modelContext?.version?.uuid),
        map((modelContext) =>
          CategoryModelUtils.getCategoryLookup(
            modelContext.version.version_data.category_tree,
          ),
        ),
      )
      .subscribe((categoryLookup) => this.categoryLookup$.next(categoryLookup));

    combineLatest([
      this.store.select(selectWorkingCategoryState),
      this.categoryLookup$,
    ])
      .pipe(
        map(([workingCategoryState, categoryLookup]) => {
          return this.getWorkingCategory(workingCategoryState, categoryLookup);
        }),
      )
      .subscribe((workingCategory) =>
        this.workingCategory$.next(workingCategory),
      );
  }

  // Version data is lazy loaded for all model context
  private injectVersionData(
    modelContext: ModelContext,
  ): Observable<ModelContext> {
    if (
      modelContext.version.version_data &&
      Object.keys(modelContext.version.version_data).length !== 0
    ) {
      return of(modelContext);
    }

    return this.earedDove.getModelVersionData(modelContext.version.uuid).pipe(
      map((version_data) => {
        return {
          ...modelContext,
          version: {
            ...modelContext.version,
            version_data,
          },
        };
      }),
      catchError(() => of(modelContext)),
    );
  }

  private getWorkingCategory(
    workingCategoryState: WorkingCategoryState,
    categoryLookup: CategoryLookup,
  ) {
    if (
      workingCategoryState?.categoryID &&
      categoryLookup &&
      workingCategoryState?.categoryID !== workingCategoryState?.sandbox.id &&
      workingCategoryState?.categoryID !==
        workingCategoryState?.deltaMode?.deltaCurrentExpression?.id
    ) {
      if (
        workingCategoryState?.categoryID ===
        workingCategoryState?.uncategorized.id
      ) {
        return workingCategoryState?.uncategorized;
      }
      return categoryLookup[workingCategoryState?.categoryID];
    }

    if (
      workingCategoryState?.categoryID ===
      workingCategoryState?.deltaMode?.deltaCurrentExpression?.id
    ) {
      // Otherwise, return the delta
      return workingCategoryState?.deltaMode?.deltaCurrentExpression;
    }

    if (workingCategoryState?.categoryID === workingCategoryState?.sandbox.id) {
      // Otherwise, return the sandbox
      return workingCategoryState?.sandbox;
    }
  }

  navigateWithContext(
    route: string[],
    relativeTo: ActivatedRoute,
    context: ContextParams,
  ) {
    this.router.navigate(route, {
      relativeTo,
      queryParams: context,
      queryParamsHandling: "merge",
    });
  }

  async setContext(context: ContextParams, id: string) {
    // setContext called
    const version = this.modelVersions.find((v) => v.uuid === id);

    // For data sources we persist the user's context under the data source id,
    // for model versions, however, we persist it under the model id.
    let storageKey = id;

    if (version) {
      storageKey = version.model_id;
    }

    let previousContextParams = this.getPreviousContextParams(storageKey);

    try {
      const navResult = await this.router.navigate([], {
        queryParams: {
          ...previousContextParams,
          ...context,
          [ContextKey.Focused]: storageKey,
        },
        queryParamsHandling: "merge",
      });
      // navigation result available in navResult
    } catch (err) {
      // navigation error
    }
  }

  getDefaultContextParams(
    id: string,
    forContext: ContextKey,
    fixedVersionID: string = undefined,
  ): Observable<ContextParams> {
    switch (forContext) {
      case ContextKey.DataSourceContextKey:
        return this.getDefaultDataSourceContext(id);
      case ContextKey.CategoryContextKey:
      case ContextKey.SentimentContextKey:
      case ContextKey.TopicContextKey:
      case ContextKey.EmotionContextKey:
      case ContextKey.IntentContextKey:
      default:
        return this.getDefaultModelVersionContext(
          id,
          forContext,
          fixedVersionID,
        );
    }
  }

  private getDefaultModelVersionContext(
    modelID: string,
    forContext: ContextKey,
    fixedVersionID: string = undefined,
  ): Observable<ContextParams> {
    // The default context for a model version should ideally be:
    //  - the data source to which the model version was most recently applied
    //  - the most recently applied model version for all model contexts *except* the specified context
    //  - the specified model version for the specified context

    // We have no way to get all data sources a given model version has been applied to
    // so we pick the best available data source from the hydrated store state.
    const selectedDataSource = this.pickDataSourceForContext(this.dataSources);

    if (selectedDataSource) {
      return this.getDefaultDataSourceContext(
        selectedDataSource.uuid,
        forContext,
        modelID,
        fixedVersionID,
      );
    }

    // Data sources are loaded but none are eligible.
    if (this.dataSources.length > 0) {
      return of(null as unknown as ContextParams);
    }

    // If the click wins the race against data-source load, wait once for hydration.
    return this.store.select(selectDataSources).pipe(
      map((dataSources) => dataSources ?? []),
      filter((dataSources) => dataSources.length > 0),
      take(1),
      mergeMap((dataSources) => {
        this.dataSources = dataSources;
        const hydratedDataSource = this.pickDataSourceForContext(dataSources);

        if (!hydratedDataSource) {
          return of(null as unknown as ContextParams);
        }

        return this.getDefaultDataSourceContext(
          hydratedDataSource.uuid,
          forContext,
          modelID,
          fixedVersionID,
        );
      }),
      catchError(() => of(null as unknown as ContextParams)),
    );
  }

  private pickDataSourceForContext(
    dataSources: DataSourceSummary[],
  ): DataSourceSummary | undefined {
    if (!dataSources?.length) {
      return undefined;
    }

    const readyDataSource = dataSources.find(
      (ds) => ds.fully_imported && !ds.stale_es_migration,
    );
    if (readyDataSource) {
      return readyDataSource;
    }

    return undefined;
  }

  private getDefaultDataSourceContext(
    dataSourceID: string,
    contextKey: ContextKey = undefined,
    modelID: string = undefined,
    fixedVersionID: string = undefined,
  ): Observable<ContextParams> {
    // For data sources we persist the user's context under the data source id,
    // for model versions, however, we persist it under the model id.
    const storageKey = modelID || dataSourceID;

    const previousContextParams: ContextParams =
      this.getPreviousContextParams(storageKey);

    // The default context for a data source is simply:
    //  - the specified data source for the data source context
    //  - the most recently applied model version for all model contexts
    return this.earedDove.getAppliedModels(dataSourceID).pipe(
      map((appliedModels: ModelsApplied) => {
        const result: ContextParams = this.getModelsContextParams(
          previousContextParams,
          appliedModels,
        );

        result.ds = this.getDataSourceContextParam(
          previousContextParams,
          dataSourceID,
        );

        // If a specific version ID has specified we want to use this
        // and if not and we did not have persisted context we want
        // to default to the working version for the context we are
        // focused on
        if (fixedVersionID) {
          result[contextKey] = fixedVersionID;
        } else if (contextKey && !previousContextParams[contextKey]) {
          result[contextKey] = this.models.find(
            (m) => m.uuid === modelID,
          ).working_version;
        }

        result[ContextKey.Focused] = storageKey;

        return result;
      }),
    );
  }

  private getModelsContextParams(
    previousContextParams: ContextParams,
    appliedModels: ModelsApplied,
  ) {
    const modelVersions = this.modelVersions ?? [];
    const keyToAppliedModels = {
      [ContextKey.CategoryContextKey]: appliedModels.category,
      [ContextKey.SentimentContextKey]: appliedModels.sentiment,
      [ContextKey.TopicContextKey]: appliedModels.topic,
      [ContextKey.EmotionContextKey]: appliedModels.emotion,
      [ContextKey.IntentContextKey]: appliedModels.intent,
    };

    const modelsContextParams: ContextParams = {};
    for (const key in keyToAppliedModels) {
      if (
        key in previousContextParams &&
        this.isValidContextVersion(
          previousContextParams[key],
          keyToAppliedModels[key],
          modelVersions,
        )
      ) {
        modelsContextParams[key] = previousContextParams[key];
      }

      if (!(key in modelsContextParams)) {
        modelsContextParams[key] = this.getLatestAppliedModelVersionId(
          keyToAppliedModels[key],
        );
      }
    }

    return modelsContextParams;
  }

  private getDataSourceContextParam(
    previousContextParams,
    defaultDataSourceID,
  ): string {
    const dataSources = this.dataSources ?? [];
    if (ContextKey.DataSourceContextKey in previousContextParams) {
      if (
        dataSources.some(
          (ds) =>
            ds.uuid === previousContextParams[ContextKey.DataSourceContextKey],
        )
      ) {
        return previousContextParams[ContextKey.DataSourceContextKey];
      }
    }

    return defaultDataSourceID;
  }

  private getLatestAppliedModelVersionId(appliedModels: AppliedModel[]) {
    const modelVersions = this.modelVersions ?? [];
    const sortedAndReduced = this.reduceAppliedModelVersions(appliedModels)
      .filter(
        (am) =>
          !modelVersions.length ||
          modelVersions.some((v) => v.uuid === am.version_id),
      )
      .sort((a, b) => {
        return Number(b.applied_at) - Number(a.applied_at);
      });
    if (sortedAndReduced.length) {
      return sortedAndReduced[0].version_id;
    }
  }

  private isValidContextVersion(
    versionID: string,
    appliedModels: AppliedModel[],
    modelVersions: ModelVersion[],
  ) {
    if (modelVersions.length) {
      return modelVersions.some((version) => version.uuid === versionID);
    }

    return this.reduceAppliedModelVersions(appliedModels).some(
      (appliedVersion) => appliedVersion.version_id === versionID,
    );
  }

  private reduceAppliedModelVersions(appliedModels: AppliedModel[]) {
    return appliedModels.reduce(
      (versions: AppliedVersion[], model: AppliedModel) => {
        return [...versions, ...model.versions];
      },
      [],
    );
  }

  private getPreviousContextParams(id: string) {
    const localStorageContext = localStorage.getItem(id);

    let previousContextParams: { [param: string]: string } = {};
    if (localStorageContext) {
      previousContextParams = JSON.parse(localStorageContext);
    }

    return previousContextParams;
  }

  getModelAndVersionOptions(context: any, modelState: ModelState) {
    const modelAndVersionOptions =
      ContextUtils.getInitialModelAndVersionOptions();
    this.earedDove
      .getAppliedModels(context.ds.uuid)
      .subscribe((modelsApplied: ModelsApplied) => {
        for (const modelType of this.selectableModels) {
          modelAndVersionOptions[modelType] =
            ContextUtils.getTypesModelsWithVersions(
              modelType,
              modelsApplied[modelType],
              context,
              modelState,
            );
        }
      });
    return modelAndVersionOptions;
  }
}
